import React, { useState, useEffect } from 'react';
import { Cookie, X, Check } from 'lucide-react';
import { TIMING_CONSTANTS } from '../constants';

interface CookieConsentBannerProps {
  onAccept: () => void;
  onDecline: () => void;
}

const CONSENT_KEY = 'cookie-consent';
const CONSENT_VALUES = {
  ACCEPTED: 'accepted',
  DECLINED: 'declined'
} as const;

export default function CookieConsentBanner({ onAccept, onDecline }: CookieConsentBannerProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const existingConsent = localStorage.getItem(CONSENT_KEY);
    
    if (!existingConsent) {
      const timer = setTimeout(() => {
        setIsVisible(true);
        setIsAnimating(true);
      }, TIMING_CONSTANTS.COOKIE_BANNER_INITIAL_DELAY);
      
      return () => clearTimeout(timer);
    } else if (existingConsent === CONSENT_VALUES.ACCEPTED) {
      onAccept();
    }
  }, [onAccept]);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, CONSENT_VALUES.ACCEPTED);
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onAccept();
    }, TIMING_CONSTANTS.COOKIE_BANNER_ANIMATION_DELAY);
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, CONSENT_VALUES.DECLINED);
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onDecline();
    }, TIMING_CONSTANTS.COOKIE_BANNER_ANIMATION_DELAY);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none">
      <div 
        className={`max-w-md w-full bg-appines-white rounded-xl shadow-2xl border-2 border-appines-emerald pointer-events-auto transform transition-all duration-500 ease-out ${
          isAnimating ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-full opacity-0 scale-95'
        }`}
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
      >
        <div className="p-6 pb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-appines-glass/25 rounded-full">
              <Cookie className="w-6 h-6 text-appines-petrol" />
            </div>
            <h2 id="cookie-consent-title" className="text-xl font-bold text-appines-petrol">
              Salut c'est nous...<br />
              les Cookies !
            </h2>
          </div>
          
          <p id="cookie-consent-description" className="text-appines-petrol/65 text-sm leading-relaxed mb-6">
            On a attendu d'être sûrs que le contenu de ce site vous intéresse avant de vous déranger, 
            mais on aimerait bien vous accompagner pendant votre visite...<br />
            <span className="font-medium text-appines-petrol mt-2 block">
              C'est OK pour vous ?
            </span>
          </p>
        </div>

        <div className="px-6 pb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleDecline}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[rgba(1,42,54,0.06)] text-appines-petrol rounded-lg hover:bg-[rgba(1,42,54,0.10)] transition-colors font-medium text-sm touch-manipulation"
              aria-label="Refuser les cookies de suivi"
            >
              <X className="w-4 h-4" />
              Non merci
            </button>
            
            <button
              onClick={handleAccept}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-appines-emerald text-appines-petrol rounded-lg hover:bg-[#22b886] transition-colors font-medium text-sm shadow-lg touch-manipulation"
              aria-label="Accepter les cookies de suivi"
            >
              <Check className="w-4 h-4" />
              Ok pour moi
            </button>
          </div>
        </div>

        <div className="px-6 pb-4 border-t border-[rgba(1,42,54,0.08)]">
          <p className="text-xs text-appines-petrol/45 text-center mt-3">
            En acceptant, vous autorisez l'utilisation de Google Analytics pour améliorer votre expérience. 
            Vos données sont traitées conformément au RGPD.
          </p>
        </div>
      </div>
    </div>
  );
}

export function useCookieConsent() {
  const [consentStatus, setConsentStatus] = useState<string | null>(null);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    setConsentStatus(consent);
  }, []);

  const hasConsented = consentStatus === CONSENT_VALUES.ACCEPTED;
  const hasDeclined = consentStatus === CONSENT_VALUES.DECLINED;
  const hasChoiceMade = hasConsented || hasDeclined;

  return {
    hasConsented,
    hasDeclined,
    hasChoiceMade,
    consentStatus
  };
}

export function resetCookieConsent() {
  localStorage.removeItem(CONSENT_KEY);
  window.location.reload();
}
